import React from 'react';
import { Dropdown, Header, List, Segment } from 'semantic-ui-react'
import { CalculationExpUtil } from '../../../../../../../utils/descriptor/calculationExp';
import Utils from '../../../../../../../utils/descriptor/descriptorUtils';
import ScaleUtil from '../../../../../../../utils/scaleUtil'
import { Scale } from '../../../../../../../constnats/generalConstants';
import { DescriptorProps } from '../../../../../../../constnats/descriptor';
import DescriptorType from '../../../../../../../utils/descriptor/descriptorType';
import { hasCategorizedChildren } from '../../../../../../../utils/categorization/descriptorsCategorization';
import ObjCheck from '../../../../../../../utils/objCheck';
import BridgeIndicators from './components/bridgeIndicators';

const WARN_CLASS = 'warn'
const ERROR_CLASS = 'legend-error'

function FieldOptions({ options }) {
    options = options.map(op => {
        const opName = op.name
        return {
            key: op.idx,
            size: "mini",
            text: opName,
            value: opName,
            onClick: op.onClick
        }
    })

    return (
        <Dropdown
            className='icon fieldOps'
            size="mini"
            icon={options.length > 0 ? "ellipsis vertical" : ""}
            options={options}
            trigger={<></>}
        />
    );

}

function TypeLegend({ descriptor }) {
    return <FieldLegendSpan label="Type" value={descriptor[DescriptorProps.TYPE]} />
}

function InputScaleLegend({ descriptor }) {
    return (
        <FieldLegendSpan
            label="Input Scale"
            value={ScaleUtil.numToTextRepresentation(descriptor[DescriptorProps.INPUT_SCALE], true).toUpperCase()} />
    )
}

function ViewScaleLegend({ descriptor }) {
    let classes = ""
    const viewScale = descriptor[DescriptorProps.VIEW_SCALE]
    if (viewScale !== descriptor[DescriptorProps.INPUT_SCALE]) {
        classes = WARN_CLASS
    }

    return (
        <FieldLegendSpan label="View Scale"
            value={ScaleUtil.numToTextRepresentation(viewScale, true).toUpperCase()}
            classes={classes} />
    )
}

function ScalableLegend({ descriptor }) {
    let classes = ""
    const scalable = descriptor[DescriptorProps.SCALABLE]
    const category = descriptor[DescriptorProps.CATEGORY]

    if (scalable === true &&
        descriptor[DescriptorProps.VIEW_SCALE] === Scale.ACTUAL) {
        classes = WARN_CLASS
    } else if (category && category?.scalable != scalable) {
        classes = ERROR_CLASS
    }

    return (
        <FieldLegendSpan label="Scalable"
            value={scalable === true}
            classes={classes} />
    )
}

function NoteLegend({ descriptor }) {
    return (
        <FieldLegendSpan label="Note"
            value={descriptor[DescriptorProps.NOTE] === true} />
    )
}

function ImportanceLegend({ descriptor }) {
    return (
        <FieldLegendSpan label="Importance"
            value={descriptor[DescriptorProps.IMPORTANCE]} />
    )
}

function CalcTypeLegend({ descriptor }) {
    let classes = ""
    const category = descriptor[DescriptorProps.CATEGORY]
    const categoryCalcType = category?.p2pCalcType
    const descriptorCalcType = descriptor[DescriptorProps.DATA_CALC_TYPE]

    if (categoryCalcType && categoryCalcType !== descriptorCalcType) {
        classes = ERROR_CLASS
    } else if (descriptorCalcType === "NA") {
        classes = WARN_CLASS
    }

    return (
        <FieldLegendSpan
            label="Calc. Method"
            value={descriptorCalcType}
            classes={classes} />
    )
}

function StandardizedLabelLegend({ standardizedLabelRequired, standardizedLabel }) {
    const hasError = standardizedLabelRequired && !standardizedLabel;
    return (
        <FieldLegendSpan
            label="Standardized Label"
            value={hasError ? 'Missing Standardized Label' : standardizedLabel.name}
            classes={hasError ? ERROR_CLASS : undefined}
        />
    );
}

function CategoryLegend({ descriptor, categorizationRequired }) {
    const category = descriptor[DescriptorProps.CATEGORY]
    const categoryName = category?.name

    const getClasses = () => {
        let classes = ""
        if(!categoryName) {
            classes = ERROR_CLASS
        }else if (hasCategorizedChildren(descriptor)) {
            classes = WARN_CLASS
        }
    }
    if (!category && !descriptor[DescriptorProps.CATEGORY_CONFIGURED] && categorizationRequired && !DescriptorType.isHeadline(descriptor)) {
        return <FieldLegendSpan label="Category"
            classes={ERROR_CLASS}
            value={"Missing Category"} />
    }

    return category &&
        <FieldLegendSpan label="Category"
            classes={getClasses()}
            value={categoryName || "ERROR"} />
}

function FieldLegendSpan({ label, value, classes = '' }) {
    return <span className={'descriptorLegendSpan ' + classes}>
        <b>{label}: </b>{String(value)}
    </span >
}

function FieldLegend({ descriptor, additioanLegend, categorizationRequired, standardizedLabelRequired,
    standardizedLabel, bridgeIndicators }) {
    const shouldRenderStandardizedLabel = (standardizedLabelRequired || standardizedLabel) && !DescriptorType.isHeadline(descriptor);

    return (
        <List.Content>
            <Header as='h5'>{Utils.getDescriptorLabel(descriptor)}</Header>
            {descriptor.editable === true &&
                <p className='fieldLegendDetails'>
                    <TypeLegend {...{ descriptor }} />
                    {DescriptorType.isNumber(descriptor) && <>
                        <InputScaleLegend {...{ descriptor }} />
                        <ViewScaleLegend  {...{ descriptor }} />
                        <ScalableLegend {...{ descriptor }} />
                        <NoteLegend {...{ descriptor }} />
                    </>}
                    <ImportanceLegend {...{ descriptor }} />
                    {!DescriptorType.isHeadline(descriptor) && <>
                        <CalcTypeLegend {...{ descriptor }} />
                    </>}
                    <CategoryLegend {...{ descriptor, categorizationRequired }} />
                    {shouldRenderStandardizedLabel && (
                        <StandardizedLabelLegend standardizedLabelRequired={standardizedLabelRequired} standardizedLabel={standardizedLabel} />
                    )}
                    {bridgeIndicators}
                    <br />
                    {additioanLegend}
                </p>}
        </List.Content>
    )
}

function CalculationSpan({ descriptor, flatDescriptors, calculationProp, calculationName, highlite }) {
    const calculation = descriptor[calculationProp]
    const humanReadableCalc = CalculationExpUtil.originalToHumanReadable(calculation, flatDescriptors)
    const dependancyIds = Utils.dependancyList(descriptor.id, flatDescriptors)
    const allowedDesctrptorsIds = flatDescriptors
        .filter(d => DescriptorType.isNumber(d))
        .filter(d => !dependancyIds.has(d.id))
        .map(d => d.id)
    const includedIds = CalculationExpUtil.getIncludedIds(calculation)
    const invalidCalc = includedIds.some(id => !allowedDesctrptorsIds.includes(id))

    const classes = invalidCalc ? ERROR_CLASS : ''

    return <span className={classes} onMouseEnter={() => highlite(includedIds)}
        onMouseLeave={() => highlite([])}><b>{calculationName}: </b>{humanReadableCalc}</span>
}

function DescriptorView({ descriptor, flatDescriptors, highlite, options, subItems, categorizationRequired,
    standardizedLabelRequired, standardizedLabel, dragHandle, bridges, additionalStyles }) {

    let additioanLegend = <></>
    if (Utils.hasCalculation(descriptor)) {
        additioanLegend = <>
            <CalculationSpan
                {...{ highlite, descriptor, flatDescriptors }}
                calculationName="Calculation"
                calculationProp={DescriptorProps.CALCULATION} />
            {Utils.hasSecondaryCalculation(descriptor) && (
                <>
                    <br />
                    <CalculationSpan
                        {...{ highlite, descriptor, flatDescriptors }}
                        calculationName="Secondary Calculation"
                        calculationProp={DescriptorProps.SECONDARY_CALCULATION} />
                </>)
            }
        </>
    }

    return <div style={{ ...descriptor.style, ...additionalStyles }}>
        <FieldOptions {...{ options }} />
        {dragHandle}
        <FieldLegend
            {...{ descriptor, additioanLegend, categorizationRequired, standardizedLabelRequired, standardizedLabel }}
            bridgeIndicators={bridges
                ? <BridgeIndicators descriptor={descriptor} bridges={bridges} onHighlightDescriptors={highlite} /> : null}
        />
        {descriptor[DescriptorProps.SUBFIELDS]?.length > 0 && <Segment>{subItems}</Segment>}
    </div >
}

class Option {
    constructor(name, onClick) {
        this.name = name
        this.onClick = onClick
    }
}

export { DescriptorView, Option }



